import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { format, startOfYear, endOfYear, isSameDay } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { useYearlyActivityWeeks } from "../../hooks/useYearlyActivityWeeks";
import { TruncatedText } from "../../../../common/components/TruncatedText/TruncatedText";
import { FloatingTooltip } from "../../../../common/components/Tooltip/FloatingTooltip";

export interface EntityHeatmapData {
  entityId: number | string;
  entityName: string;
  colorGradient: string[];
  primaryColor: string;
  sessions: { date: Date }[];
}

interface ActivityHeatmapComparisonProps {
  entities: EntityHeatmapData[];
}

const ActivityHeatmapComparison = ({ entities }: ActivityHeatmapComparisonProps) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "fr" ? fr : enUS;

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  const goToPrevYear = () => setYear((prev) => prev - 1);
  const goToNextYear = () => setYear((prev) => Math.min(prev + 1, currentYear));
  const canGoNext = year < currentYear;

  const dayLabels =
    i18n.language === "fr"
      ? ["Lun", "", "Mer", "", "Ven", "", ""]
      : ["Mon", "", "Wed", "", "Fri", "", ""];

  return (
    <div>
      <div className="flex items-center justify-center gap-4 mb-4">
        <FloatingTooltip content={t("statsNew.charts.prevYear")} ariaHidden>
          <button
            onClick={goToPrevYear}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            aria-label={t("statsNew.charts.prevYear")}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
        </FloatingTooltip>
        <span className="text-sm font-semibold text-gray-900 min-w-[60px] text-center">{year}</span>
        <FloatingTooltip content={t("statsNew.charts.nextYear")} ariaHidden wrap>
          <button
            onClick={goToNextYear}
            disabled={!canGoNext}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label={t("statsNew.charts.nextYear")}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        </FloatingTooltip>
      </div>

      <div className="flex flex-wrap gap-6 justify-center">
        {entities.map((entity) => (
          <SingleHeatmap
            key={entity.entityId}
            entity={entity}
            year={year}
            locale={locale}
            dayLabels={dayLabels}
            t={t}
          />
        ))}
      </div>
    </div>
  );
};

interface SingleHeatmapProps {
  entity: EntityHeatmapData;
  year: number;
  locale: typeof fr;
  dayLabels: string[];
  t: TFunction;
}

const SingleHeatmap = ({ entity, year, locale, dayLabels, t }: SingleHeatmapProps) => {
  const sessionCountsByDay = useMemo(() => {
    const counts = new Map<string, number>();

    entity.sessions.forEach((session) => {
      const dateKey = format(session.date, "yyyy-MM-dd");
      counts.set(dateKey, (counts.get(dateKey) || 0) + 1);
    });

    return counts;
  }, [entity.sessions]);

  const { maxCount, weeksData, monthLabels } = useYearlyActivityWeeks(
    sessionCountsByDay,
    year,
    locale,
  );

  const getColorStyle = (count: number, inYear: boolean): React.CSSProperties => {
    if (!inYear) return { backgroundColor: "transparent" };
    if (count === 0) return { backgroundColor: "white", border: "1px solid #e5e7eb" };

    const intensity = count / maxCount;
    if (intensity <= 0.25) return { backgroundColor: entity.colorGradient[0] };
    if (intensity <= 0.5) return { backgroundColor: entity.colorGradient[1] };
    if (intensity <= 0.75) return { backgroundColor: entity.colorGradient[2] };
    return { backgroundColor: entity.colorGradient[3] };
  };

  const totalSessionsThisYear = useMemo(() => {
    let total = 0;
    const yearStart = startOfYear(new Date(year, 0, 1));
    const yearEnd = endOfYear(new Date(year, 0, 1));

    sessionCountsByDay.forEach((count, dateKey) => {
      const date = new Date(dateKey);
      if (date >= yearStart && date <= yearEnd) {
        total += count;
      }
    });

    return total;
  }, [sessionCountsByDay, year]);

  return (
    <div className="flex-shrink-0">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entity.primaryColor }} />
        <TruncatedText
          as="h4"
          className="text-sm font-semibold text-gray-900 max-w-[200px]"
          text={entity.entityName}
        />
        <span className="text-xs text-gray-500">
          {totalSessionsThisYear.toLocaleString()} {t("statsNew.heatmap.sessionsIn")} {year}
        </span>
      </div>

      <div className="overflow-x-auto pb-2">
        <div className="inline-flex flex-col min-w-max">
          <div className="ml-7 mb-1 h-4 relative">
            {monthLabels.map(({ label, colStart }, idx) => (
              <span
                key={idx}
                className="text-xs text-gray-500 absolute whitespace-nowrap"
                style={{ left: `${colStart * 12}px` }}
              >
                {label}
              </span>
            ))}
          </div>

          <div className="flex">
            <div className="flex flex-col gap-[2px] mr-1 text-[10px] text-gray-500">
              {dayLabels.map((label, idx) => (
                <div
                  key={idx}
                  className="h-[10px] flex items-center justify-end pr-1"
                  style={{ width: "24px" }}
                >
                  {label}
                </div>
              ))}
            </div>

            <div className="flex gap-[2px]">
              {weeksData.map((week, weekIndex) => (
                <div key={weekIndex} className="flex flex-col gap-[2px]">
                  {week.map((day, dayIndex) => {
                    const isToday = day.inYear && isSameDay(day.date, new Date());
                    const sessionLabel =
                      day.count === 0
                        ? t("statsNew.heatmap.noSession")
                        : day.count === 1
                        ? t("statsNew.heatmap.oneSession")
                        : t("statsNew.heatmap.sessions", { count: day.count });
                    const tooltipText = day.inYear
                      ? `${sessionLabel} - ${format(day.date, "EEEE d MMMM yyyy", { locale })}`
                      : "";

                    return (
                      <div
                        key={dayIndex}
                        className={`w-[10px] h-[10px] rounded-sm ${
                          isToday ? "ring-1 ring-gray-400 ring-offset-1" : ""
                        } transition-colors hover:ring-1 hover:ring-gray-300 cursor-default`}
                        style={getColorStyle(day.count, day.inYear)}
                        title={tooltipText}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-1 text-xs text-gray-500">
            <span>{t("statsNew.heatmap.less")}</span>
            <div className="flex gap-[2px]">
              <div
                className="w-[10px] h-[10px] rounded-sm"
                style={{ backgroundColor: "white", border: "1px solid #e5e7eb" }}
                title="0"
              />
              <div
                className="w-[10px] h-[10px] rounded-sm"
                style={{ backgroundColor: entity.colorGradient[0] }}
              />
              <div
                className="w-[10px] h-[10px] rounded-sm"
                style={{ backgroundColor: entity.colorGradient[1] }}
              />
              <div
                className="w-[10px] h-[10px] rounded-sm"
                style={{ backgroundColor: entity.colorGradient[2] }}
              />
              <div
                className="w-[10px] h-[10px] rounded-sm"
                style={{ backgroundColor: entity.colorGradient[3] }}
              />
            </div>
            <span>{t("statsNew.heatmap.more")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityHeatmapComparison;
